import { apiClient } from '$lib/api/client';
import { industryCentroids, industryCentroidsLoading } from '$lib/stores/data';
import { handleError } from '$lib/utils/error-handling';
import { notifications } from '$lib/stores/notifications';
import type { ApiResponse } from '$lib/api/client';

export interface IndustryCentroid {
  id: number;
  name: string;
  industry_id: number;
  industry_name: string;
  company_count?: number;
  contact_count?: number;
}

interface IndustryCentroidsResponse {
  centroids: IndustryCentroid[];
}

export interface IndustryGroup {
  id: number;
  name: string;
  centroids: IndustryCentroid[];
  totalCompanies: number;
  totalContacts: number;
}

export function groupCentroidsByIndustry(centroids: IndustryCentroid[]): IndustryGroup[] {
  const groups = new Map<number, IndustryGroup>();

  centroids.forEach((centroid) => {
    const industryId = centroid.industry_id;

    if (!groups.has(industryId)) {
      groups.set(industryId, {
        id: industryId,
        name: centroid.industry_name,
        centroids: [],
        totalCompanies: 0,
        totalContacts: 0
      });
    }

    const group = groups.get(industryId)!;
    group.centroids.push(centroid);
    group.totalCompanies += centroid.company_count || 0;
    group.totalContacts += centroid.contact_count || 0;
  });

  // Sort groups by name, then sort centroids within each group
  return Array.from(groups.values())
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((group) => ({
      ...group,
      centroids: group.centroids.sort((a, b) => a.name.localeCompare(b.name))
    }));
}

/**
 * Load the published industries.
 *
 * # It is not a search, and used to say it was
 *
 * This set `queryRunning` for the duration — the flag that means a customer's
 * search is in flight. Nothing reads that flag to show this list loading;
 * what reads it is the results screen, which takes any sighting of it as
 * proof that a search has happened (`hasEverSearched` in Results.svelte) and
 * switches the empty state from "nothing searched yet" to "your search found
 * nothing". Loading a reference list therefore told the customer their search
 * came back empty, before they had made one.
 *
 * That was invisible while only the data-selection modal called this, because
 * the modal is opened from a screen that has already searched. The guided
 * search screen loads the list before anything is searched, which is where it
 * surfaced.
 */
export async function loadPublicIndustryCentroids(): Promise<void> {
  try {
    industryCentroidsLoading.set(true);

    const response: ApiResponse<IndustryCentroidsResponse> = await apiClient.get(
      '/deep-iq/industry-centroids'
    );

    if (response.data.centroids && Array.isArray(response.data.centroids)) {
      industryCentroids.set(response.data.centroids);
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    const errorMessage = await handleError(error, {
      operation: 'loadPublicIndustryCentroids',
      component: 'data-api'
    });

    notifications.error(errorMessage);

    // Reset to empty array on error
    industryCentroids.set([]);
  } finally {
    industryCentroidsLoading.set(false);
  }
}
